import { atom, getDefaultStore, type PrimitiveAtom } from 'jotai';
import {
  fetchApiRaw,
  fetchApiRawResponse,
  type ParamsSlotFor,
  type RawRequestInit,
} from './fetchApiRaw';
import type { EndpointId, EndpointResponses, User } from './schemas';

// Session bearer token, on jotai's default store. Written by the auth
// lifecycle owner on login / logout; fetchApi reads it at call time — no
// per-call token threading.
export const accessTokenAtom: PrimitiveAtom<string | undefined> = atom<
  string | undefined
>(undefined);

// fetchApi awaits this before firing any request. Resolved once, by
// markAuthReady, when the auth lifecycle owner settles initial auth (either
// "we have a valid session" or "we're anonymous") — keeps queries from
// firing with stale/empty tokens during the initial reconnection window.
let authReadyResolve: (() => void) | undefined;
const authReadyPromise = new Promise<void>((resolve) => {
  authReadyResolve = resolve;
});

export function markAuthReady(): void {
  // Stryker disable next-line OptionalChaining: equivalent — the Promise executor runs synchronously at module init, so authReadyResolve is always assigned before any call; ?. exists only because TS can't prove the executor ran
  authReadyResolve?.();
}

const store = getDefaultStore();

// Options stay a rest tuple so ids with an optional params family keep the
// no-options call shape while ids whose family REQUIRES params (urlParams
// carriers) reject an omitted argument.
type OptionsRestFor<K extends EndpointId> =
  undefined extends ParamsSlotFor<K> ? [options?: ParamsSlotFor<K>]
  : [options: ParamsSlotFor<K>];

// The default entry: awaits the one-shot auth-ready latch, then delegates
// to fetchApiRaw with the current accessTokenAtom value (read at call
// time). NO request fires before markAuthReady() — consumers racing the
// initial-session settle just wait, they never fire token-less.
export async function fetchApi<K extends EndpointId>(
  endpointId: K,
  ...rest: OptionsRestFor<K>
): Promise<EndpointResponses[K]> {
  await authReadyPromise;
  const [options] = rest;
  return fetchApiRaw(endpointId, options, store.get(accessTokenAtom));
}

// The raw-response entry for backend routes outside the endpoint registry
// (see fetchApiRawResponse): the same auth-ready latch and call-time token
// as fetchApi, the Response handed back unread.
export async function fetchApiResponse(
  path: string,
  init: RawRequestInit,
): Promise<Response> {
  await authReadyPromise;
  return fetchApiRawResponse(path, init, store.get(accessTokenAtom));
}

// GET_TOTAL_ISSUERS filtered to the exact address string as given (an
// exact-match filter — callers pass the checksummed form), take 1: the
// first row, or null for an address no profile owns.
export async function fetchIssuerByAddress(
  address: string,
  signal: AbortSignal | undefined,
): Promise<User | null> {
  const { data } = await fetchApi('GET_TOTAL_ISSUERS', {
    signal,
    queryParams: { filter: `"address":["${address}"]`, take: '1' },
  });
  return data[0] ?? null;
}
